import React, { useState } from "react";

import {

  useLocation,

  useNavigate,

  useParams,

} from "react-router-dom";

import { useDispatch } from "react-redux";

import {

  createRazorpayOrder,

  verifyRazorpayPayment,

} from "../../actions/paymentActions";

import { useCart } from "../../components/CartContext";
import { useRestaurantSession } from "../../components/RestaurantSessionProvider";
import { useSelector } from "react-redux";

import "./css/PaymentScreen.css";

// =====================================================

// LOAD RAZORPAY CHECKOUT SCRIPT

// =====================================================

const loadRazorpayScript = () => {

  return new Promise((resolve) => {

    // Already loaded

    if (window.Razorpay) {

      resolve(true);

      return;

    }

    // Check if script already exists

    const existingScript =

      document.querySelector(

        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'

      );

    if (existingScript) {

      existingScript.addEventListener(

        "load",

        () => resolve(true)

      );

      existingScript.addEventListener(

        "error",

        () => resolve(false)

      );

      return;

    }

    // Create script

    const script =

      document.createElement("script");

    script.src =

      "https://checkout.razorpay.com/v1/checkout.js";

    script.async = true;

    script.onload = () => {

      console.log(

        "Razorpay Checkout loaded successfully"

      );

      resolve(true);

    };

    script.onerror = () => {

      console.error(

        "Failed to load Razorpay Checkout"

      );

      resolve(false);

    };

    document.body.appendChild(script);

  });

};

function PaymentScreen() {

  const navigate = useNavigate();

  const location = useLocation();

  const dispatch = useDispatch();

  const {

    restaurantId,

    tableId,

  } = useParams();

  const {

    clearCart,

  } = useCart();
  const { sessionToken } = useRestaurantSession();
  const createdOrder = useSelector((state) => state.order?.order);

  // =====================================================

  // ORDER DATA

  // =====================================================

  const {

    orderId: navigationOrderId,

    subtotal = 0,

    tax = 0,

    serviceCharge = 0,

    total = 0,

  } = location.state || {};
  const orderId = createdOrder?.id || navigationOrderId;
  const confirmedTotal = Number(createdOrder?.total ?? total);

  // =====================================================

  // STATE

  // =====================================================

  const [loading, setLoading] =

    useState(false);

  const [error, setError] =

    useState("");

  // =====================================================

  // HOME PATH

  // =====================================================

  const homePath =

    `/${restaurantId}/table/${tableId}`;

  // =====================================================

  // BACK

  // =====================================================

  const handleBack = () => {

    if (loading) {

      return;

    }

    navigate(

      `${homePath}/checkout`,

      {

        state: {

          subtotal,

          tax,

          serviceCharge,

          total,

        },

      }

    );

  };

  // =====================================================

  // PAYMENT

  // =====================================================

  const handlePayment = async () => {
    if (!sessionToken) { setError("Dining session unavailable. Please scan the table QR code again."); return; }

    if (loading) {

      return;

    }

    if (!orderId) {

      setError(

        "Order information is missing. Please go back and try again."

      );

      return;

    }

    try {

      setLoading(true);

      setError("");

      // =================================================

      // STEP 1

      // LOAD RAZORPAY

      // =================================================

      console.log(

        "Loading Razorpay Checkout..."

      );

      const razorpayLoaded =

        await loadRazorpayScript();

      if (!razorpayLoaded) {

        throw new Error(

          "Razorpay Checkout could not be loaded. Please check your internet connection or browser settings."

        );

      }

      console.log(

        "Razorpay SDK:",

        window.Razorpay

      );

      // =================================================

      // STEP 2

      // CREATE RAZORPAY ORDER

      // =================================================

      console.log(

        "Creating Razorpay order..."

      );

      const razorpayOrder =

        await dispatch(

          createRazorpayOrder(

            restaurantId,

            orderId, sessionToken

          )

        );

      console.log(

        "RAZORPAY CREATE RESPONSE:",

        razorpayOrder

      );

      // =================================================

      // VALIDATE RESPONSE

      // =================================================

      if (!razorpayOrder) {

        throw new Error(

          "Payment order was not created."

        );

      }

      if (

        !razorpayOrder.razorpay_order_id

      ) {

        throw new Error(

          "Razorpay Order ID is missing."

        );

      }

      if (

        !razorpayOrder.key_id

      ) {

        throw new Error(

          "Razorpay Key ID is missing from the server response."

        );

      }

      if (

        razorpayOrder.amount === undefined ||

        razorpayOrder.amount === null

      ) {

        throw new Error(

          "Payment amount is missing."

        );

      }

      // =================================================

      // STEP 3

      // RAZORPAY OPTIONS

      // =================================================

      const options = {

        key:

          razorpayOrder.key_id,

        amount:

          razorpayOrder.amount,

        currency:

          razorpayOrder.currency || "INR",

        // IMPORTANT:

        // This is the Razorpay order ID

        order_id:

          razorpayOrder.razorpay_order_id,

        name:

          "KAVOS TAVERNA",

        description:

          `Order #${orderId}`,

        // =================================================

        // PAYMENT SUCCESS

        // =================================================

        handler:

          async function (response) {

            try {

              setLoading(true);

              setError("");

              // =========================================

              // VERIFY PAYMENT

              // =========================================

              const paymentData = {

                razorpay_order_id:

                  response.razorpay_order_id,

                razorpay_payment_id:

                  response.razorpay_payment_id,

                razorpay_signature:

                  response.razorpay_signature,

                order_id:

                  orderId,

              };

              const verifyResponse =

                await dispatch(

                  verifyRazorpayPayment(

                    restaurantId,

                    paymentData, sessionToken

                  )

                );

              console.log(

                "RAZORPAY VERIFY RESPONSE:",

                verifyResponse

              );

              // =========================================

              // SUCCESS

              // =========================================

              clearCart();

              navigate(

                `${homePath}/success`,

                {

                  state: {

                    orderId,

                    paymentMethod:

                      "ONLINE",

                    total:

                      confirmedTotal,

                  },

                }

              );

            } catch (paymentError) {

              console.error(

                "Payment verification error:",

                paymentError

              );

              const message =

                paymentError?.response?.data?.detail ||

                paymentError?.response?.data ||

                paymentError?.message ||

                "Payment verification failed.";

              setError(

                typeof message === "string"

                  ? message

                  : "Payment verification failed."

              );

              setLoading(false);

            }

          },

        // =================================================

        // MODAL CLOSED

        // =================================================

        modal: {

          ondismiss: () => {

            console.log(

              "Razorpay checkout closed"

            );

            setLoading(false);

          },

        },

        // =================================================

        // THEME

        // =================================================

        theme: {

          color:

            "#0d5b70",

        },

      };

      console.log(

        "RAZORPAY OPTIONS:",

        {

          key: options.key,

          amount: options.amount,

          currency: options.currency,

          order_id: options.order_id,

        }

      );

      // =================================================

      // STEP 4

      // CREATE CHECKOUT

      // =================================================

      const razorpay =

        new window.Razorpay(options);

      // =================================================

      // PAYMENT FAILED

      // =================================================

      razorpay.on(

        "payment.failed",

        (response) => {

          console.error(

            "RAZORPAY PAYMENT FAILED:",

            response

          );

          const description =

            response?.error?.description ||

            "Payment failed. Please try again.";

          setError(

            description

          );

          setLoading(false);

        }

      );

      // =================================================

      // OPEN CHECKOUT

      // =================================================

      console.log(

        "Opening Razorpay Checkout..."

      );

      razorpay.open();

    } catch (paymentError) {

      console.error(

        "Payment error:",

        paymentError

      );

      const message =

        paymentError?.response?.data?.detail ||

        paymentError?.response?.data ||

        paymentError?.message ||

        "Unable to start payment. Please try again.";

      setError(

        typeof message === "string"

          ? message

          : "Unable to start payment. Please try again."

      );

      setLoading(false);

    }

  };

  // =====================================================

  // UI

  // =====================================================

  return (

    <div className="t6-payment-page">

      {/* =================================================

          HEADER

      ================================================= */}

      <div className="t6-payment-top">

        <button

          type="button"

          className="t6-payment-back"

          onClick={handleBack}

          disabled={loading || !sessionToken}

        >

          ← Back

        </button>

        <div className="t6-payment-brand">

          <span className="t6-payment-brand-main">

            KAVOS

          </span>

          <span className="t6-payment-brand-sub">

            TAVERNA

          </span>

        </div>

        <div className="t6-payment-table">

          <span>

            TABLE

          </span>

          <strong>

            {tableId}

          </strong>

        </div>

      </div>

      {/* =================================================

          MAIN

      ================================================= */}

      <main className="t6-payment-main">

        {/* =================================================

            PAYMENT CARD

        ================================================= */}

        <section className="t6-payment-card">

          <div className="t6-payment-heading">

            <span className="t6-payment-eyebrow">

              SECURE PAYMENT

            </span>

            <h1>

              Complete Your Order

            </h1>

            <p>

              Your table is waiting.

              Complete the payment securely

              to confirm your order.

            </p>

          </div>

          {/* =================================================

              ORDER

          ================================================= */}

          <div className="t6-payment-order">

            <div>

              <span className="t6-payment-label">

                ORDER

              </span>

              <strong>

                #{orderId}

              </strong>

            </div>

            <div>

              <span className="t6-payment-label">

                TABLE

              </span>

              <strong>

                {tableId}

              </strong>

            </div>

          </div>

          {/* =================================================

              SUMMARY

          ================================================= */}

          <div className="t6-payment-summary">

            <div className="t6-payment-summary-title">

              YOUR ORDER

            </div>

            <div className="t6-payment-row">

              <span>

                Subtotal

              </span>

              <strong>

                ₹{Number(subtotal).toFixed(2)}

              </strong>

            </div>

            <div className="t6-payment-row">

              <span>

                Tax

              </span>

              <strong>

                ₹{Number(tax).toFixed(2)}

              </strong>

            </div>

            <div className="t6-payment-row">

              <span>

                Service Charge

              </span>

              <strong>

                ₹{Number(serviceCharge).toFixed(2)}

              </strong>

            </div>

            <div className="t6-payment-divider" />

            <div className="t6-payment-total">

              <span>

                TOTAL

              </span>

              <strong>

                ₹{Number(confirmedTotal).toFixed(2)}

              </strong>

            </div>

          </div>

          {/* =================================================

              PAYMENT METHOD

          ================================================= */}

          <div className="t6-payment-method">

            <div className="t6-payment-method-icon">

              ₹

            </div>

            <div className="t6-payment-method-info">

              <strong>

                Online Payment

              </strong>

              <span>

                Secure payment powered by Razorpay

              </span>

            </div>

            <div className="t6-payment-secure">

              🔒

            </div>

          </div>

          {/* =================================================

              ERROR

          ================================================= */}

          {error && (

            <div className="t6-payment-error">

              <strong>

                Payment unsuccessful

              </strong>

              <span>

                {error}

              </span>

            </div>

          )}

          {/* =================================================

              PAY

          ================================================= */}

          <button

            type="button"

            className="t6-pay-button"

            onClick={handlePayment}

            disabled={loading || !sessionToken}

          >

            {loading ? (

              <>

                <span className="t6-payment-spinner"></span>

                Processing...

              </>

            ) : (

              <>

                Pay ₹{Number(confirmedTotal).toFixed(2)}

                <span>

                  →

                </span>

              </>

            )}

          </button>

          {/* =================================================

              SECURITY

          ================================================= */}

          <div className="t6-payment-security">

            <span>

              🔒

            </span>

            <p>

              Your payment is processed securely.

              We never store your card details.

            </p>

          </div>

        </section>

        {/* =================================================

            SIDE PANEL

        ================================================= */}

        <aside className="t6-payment-aside">

          <div className="t6-payment-aside-mark">

            K

          </div>

          <span>

            KAVOS TAVERNA

          </span>

          <h2>

            A taste of

            <br />

            the Mediterranean.

          </h2>

          <p>

            Fresh seafood, local ingredients

            and the warmth of the Greek coast.

          </p>

        </aside>

      </main>

      {/* =================================================

          FOOTER

      ================================================= */}

      <footer className="t6-payment-footer">

        <span>

          KAVOS TAVERNA

        </span>

        <span>

          FRESH · LOCAL · MEDITERRANEAN

        </span>

      </footer>

    </div>

  );

}

export default PaymentScreen;